import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';

function Survey() {
  const navigate = useNavigate();
  const { referralCode } = useParams(); // Get the referral code from the URL
  const steps = useMemo(() => [
    "Contact Information",
    "Home Address",
    "Energy Providers",
    "Household & Eligibility",
    "Finish",
  ], []);
  // Google Places Autocomplete setup
  const streetInputRef = useRef(null);
  const [placesLoaded, setPlacesLoaded] = useState(false);
  const acRef = useRef(null); // store the Autocomplete instance so we don't double-init

    // State to manage the current step
  const [currentStep, setCurrentStep] = useState(0);

  // Form data (all survey responses)
  const [formData, setFormData] = useState(() => {
    const shouldResume = localStorage.getItem("resumeSurvey") === "true";
    if (shouldResume) {
      localStorage.removeItem("resumeSurvey");
      const saved = localStorage.getItem("userInfo");
      return saved ? JSON.parse(saved) : {
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "", 
        streetAddress: "",
        city: "",
        zipCode: "",
        recentAssessment: "", // Options: "never", "lt2", "2t5", "gt5"
        electricityProvider: "", // Options: "ever", "ngrid", "muni"
        gasProvider: "ngrid", // Options: "ngrid", "ever", "none"
        fuelSource: "", // Options: "gas", "oil", "propane", "electric"
        paymentAssistance: "", // Options: "y", "n"
        multiUnit: "", // Options: "1", "2", "3", "4", "5plus"
        rentOwn: "", // Options: "rent", "own"
        condo: "", // Options: "y", "n"
        referralCode: "",
        otherInfo: "",
        hearAbout: "",
        hearAboutOther: "",
        preferredLanguage: "",
        preferredLanguageOther: "",
        heatPumpInterest: "",

      };
    }
    // Default: blank form
    return {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "", 
      streetAddress: "",
      city: "",
      zipCode: "",
      recentAssessment: "", // Options: "never", "lt2", "2t5", "gt5"
      electricityProvider: "", // Options: "ever", "ngrid", "muni"
      gasProvider: "ngrid", // Options: "ngrid", "ever", "none"
      fuelSource: "", // Options: "gas", "oil", "propane", "electric"
      paymentAssistance: "", // Options: "y", "n"
      multiUnit: "", // Options: "1", "2", "3", "4", "5plus"
      rentOwn: "", // Options: "rent", "own"
      condo: "", // Options: "y", "n"
      referralCode: "",
      otherInfo: "",
      hearAbout: "",
      hearAboutOther: "",
      preferredLanguage: "",
      preferredLanguageOther: "",
      heatPumpInterest: "",

    };
  });

  // referralCode is passed in the URL, so we need to set it in the formData state
  // This is done in the useEffect hook below
  useEffect(() => {
    if (referralCode) {
      setFormData(f => ({
        ...f,
        referralCode
      }));
    }
  }, [referralCode]);

  // --- Load Google Places script once (safe) ---
  useEffect(() => {
    if (process.env.REACT_APP_PLACES_AUTOCOMPLETE_ENABLED !== 'true') return;

    // Already present?
    if (window.google?.maps?.places) {
      setPlacesLoaded(true);
      return;
    }

    // Reuse existing <script> if it’s already on the page
    let script = document.querySelector('#google-maps-js');
    if (script) {
      const onLoad = () => setPlacesLoaded(true);
      const onError = () => console.error("Google Maps script failed to load");
      script.addEventListener('load', onLoad, { once: true });
      script.addEventListener('error', onError, { once: true });
      return () => {
        script.removeEventListener('load', onLoad);
        script.removeEventListener('error', onError);
      };
    }

    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!key) {
      console.error("Missing REACT_APP_GOOGLE_MAPS_API_KEY");
      return;
    }

    script = document.createElement("script");
    script.id = "google-maps-js";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => setPlacesLoaded(true);
    script.onerror = () => console.error("Google Maps script failed to load");
    document.body.appendChild(script);
  }, []);

  // --- Initialize Google Autocomplete (only on "Home Address" step) ---
  useEffect(() => {
    if (process.env.REACT_APP_PLACES_AUTOCOMPLETE_ENABLED !== 'true') return;
    if (currentStep !== 1) return;          // Only when the Home Address step is visible
    if (acRef.current) return;              // Already initialized

    let tries = 0;
    const MAX_TRIES = 40; // 4 seconds max (40 * 100ms)

    const tryInit = () => {
      const hasLib = !!(window.google && window.google.maps && window.google.maps.places);
      const inputEl = streetInputRef.current;

      if (hasLib && inputEl && !acRef.current) {
        try {
          const ac = new window.google.maps.places.Autocomplete(inputEl, {
            types: ["address"],
            componentRestrictions: { country: "us" },
            fields: ["address_components", "formatted_address", "geometry"],
          });

          ac.addListener("place_changed", () => {
            const place = ac.getPlace?.();
            if (!place) return;
            const parsed = parsePlaceAddress(place);
            setFormData(prev => ({
              ...prev,
              streetAddress: parsed.streetAddress || prev.streetAddress,
              city: parsed.city || prev.city,
              zipCode: parsed.zipCode || prev.zipCode,
              _addrFromAutocomplete: true,
            }));
          });

          acRef.current = ac;
          clearInterval(timer);
        } catch (e) {
          console.error("[GMAPS] init error:", e);
        }
      }

      if (++tries >= MAX_TRIES) {
        clearInterval(timer);
      }
    };

    const timer = setInterval(tryInit, 100);
    return () => clearInterval(timer);
  }, [currentStep, setFormData]);


  // Simple validation errors
  const [errors, setErrors] = useState({});

  // Helper functions for special error conditions
  const validZips = ["02019", "02021", "02025", "02026", "02027", "02030", 
    "02032", "02035", "02038", "02052", "02053", "02054", "02056", "02062", 
    "02067", "02070", "02071", "02072", "02081", "02090", "02093", "02169", 
    "02170", "02171", "02184", "02185", "02186", "02187", "02188", "02189", 
    "02190", "02191", "02269", "02322", "02324", "02339", "02343", "02368", 
    "02445", "02446", "02447", "02457", "02467", "02481", "02482", "02492", 
    "02494", "02762", "02108", "02109", "02110", "02111", "02112", "02113", 
    "02114", "02115", "02116", "02117", "02118", "02119", "02120", "02121", 
    "02122", "02123", "02124", "02125", "02126", "02127", "02128", "02129", 
    "02130", "02131", "02132", "02133", "02134", "02135", "02136", "02137", 
    "02150", "02151", "02152", "02163", "02196", "02199", "02201", "02203", 
    "02204", "02205", "02206", "02210", "02211", "02212", "02215", "02217", 
    "02222", "02241", "02283", "02284", "02293", "02297", "02298", "02018", 
    "02020", "02040", "02041", "02043", "02044", "02045", "02047", "02050", 
    "02051", "02055", "02059", "02060", "02061", "02065", "02066", "02301", 
    "02302", "02303", "02304", "02305", "02327", "02330", "02331", "02332", 
    "02333", "02337", "02338", "02341", "02344", "02345", "02346", "02347", 
    "02348", "02349", "02350", "02351", "02355", "02358", "02359", "02360", 
    "02361", "02362", "02364", "02366", "02367", "02370", "02379", "02381", 
    "02382", "02538", "02558", "02571", "02576", "02738", "02739", "02770", 
    "02031", "02048", "02334", "02356", "02357", "02375", "02702", "02703", 
    "02712", "02714", "02715", "02717", "02718", "02719", "02720", "02721", 
    "02722", "02723", "02724", "02725", "02726", "02740", "02741", "02742", 
    "02743", "02744", "02745", "02746", "02747", "02748", "02760", "02761", 
    "02763", "02764", "02766", "02767", "02768", "02769", "02771", "02777", 
    "02779", "02780", "02790", "02791", "01431", "01432", "01434", "01450", 
    "01460", "01463", "01464", "01469", "01472", "01474", "01701", "01702", 
    "01703", "01704", "01705", "01718", "01719", "01720", "01721", "01730", 
    "01731", "01741", "01742", "01746", "01748", "01749", "01752", "01754", 
    "01760", "01770", "01773", "01775", "01776", "01778", "01784", "01801", 
    "01803", "01805", "01813", "01815", "01821", "01822", "01824", "01826", 
    "01827", "01850", "01851", "01852", "01853", "01854", "01862", "01863", 
    "01864", "01865", "01866", "01867", "01876", "01879", "01880", "01886", 
    "01887", "01888", "01889", "01890", "02138", "02139", "02140", "02141", 
    "02142", "02143", "02144", "02145", "02148", "02149", "02153", "02155", 
    "02156", "02176", "02177", "02180", "02238", "02420", "02421", "02451", 
    "02452", "02453", "02454", "02455", "02456", "02458", "02459", "02460", 
    "02461", "02462", "02464", "02465", "02466", "02468", "02471", "02472", 
    "02474", "02475", "02476", "02477", "02478", "02479", "02493", "02495",
    "01451", "01453", "01503", "01505", "01510", "01516", "01519", "01520",
    "01523", "01527", "01529", "01532", "01534", "01541", "01545", "01561", 
    "01564", "01568", "01569", "01581", "01583", "01590", "01601", "01602", 
    "01603", "01604", "01605", "01606", "01607", "01608", "01609", "01610", 
    "01612", "01613", "01614", "01615", "01655", "01740", "01747", "01756", 
    "01757", "01810", "01830", "01832", "01833", "01834", "01840", "01841", 
    "01843", "01844", "01845", "01901", "01902", "01904", "01905", "01906", 
    "01907", "01908", "01913", "01915", "01921", "01923", "01929", "01930", 
    "01936", "01938", "01940", "01944", "01945", "01949", "01950", "01951", 
    "01952", "01960", "01961", "01966", "01969", "01970", "01971", "01983", 
    "01984", "01985"
  ];

  
  function zipIsValid(zip) {
      for (let i = 0; i < validZips.length; i++) {
          if (validZips[i].includes(zip)) {
              return true;
          }
      }
      return false;
  }
  // Error Type 1 conditions:
  // - Recent assessment is less than 2 years ago ("lt2")
  // - Payment assistance is "Yes" ("y")
  // - Home is in a multi-unit building (we assume "y" means large multi-family)
  // - Fuel source is Electric, Oil, or Propane AND Electricity Provider is Municipal ("muni")
  // - Home is located in a condo association ("y")

  const isErrorType1 = (data) => {
    return (
      data.recentAssessment === "lt2" ||
      data.paymentAssistance === "y" ||
      data.multiUnit === "5plus" ||
      ((data.fuelSource === "electric" || data.fuelSource === "oil" || data.fuelSource === "propane") &&
       data.electricityProvider === "muni") ||
      data.condo === "y" ||
      !zipIsValid(data.zipCode)
    );
  };

  const getErrorType1Flags = (data) => {
    const flags = [];
    if (data.recentAssessment === "lt2") flags.push("recentAssessment");
    if (data.paymentAssistance === "y") flags.push("paymentAssistance");
    if (data.multiUnit === "5plus") flags.push("multiUnit");
    if (["electric","oil","propane"].includes(data.fuelSource) && data.electricityProvider === "muni") {
      flags.push("fuelMunicipal");
    }
    if (data.condo === "y") flags.push("condo");
    if (!zipIsValid(data.zipCode)) flags.push("invalidZip");
    return flags;
  };

  // (Error Type 3 and default routing are the same for our purposes, so no special check is needed here.)

  // Validation function (for each step)
  const validateCurrentStep = () => {
    let newErrors = {};
    if (currentStep === 0) { // Contact Information
      if (!formData.firstName.trim()) {
        newErrors.firstName = "First name is required.";
      }
      if (!formData.lastName.trim()) {
        newErrors.lastName = "Last name is required.";
      }
      if (!formData.email.trim()) {
        newErrors.email = "Email is required.";
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = "Email format is invalid.";
      }
      if (!formData.phoneNumber.trim()) {
        newErrors.phoneNumber = "Phone number is required.";
      } else if (!/^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/.test(formData.phoneNumber)) {
        newErrors.phoneNumber = "Phone number format is invalid.";
      }
    }
    else if (currentStep === 1) { // Home Address
      const addressRegex = /^[0-9]+(?: [A-Za-z0-9#.,-]+)+(?:,? [A-Za-z]+)?\s*$/;
      if (!formData.streetAddress.trim()) {
        newErrors.streetAddress = "Street Address is required.";
      } else if (!addressRegex.test(formData.streetAddress)) {
        newErrors.streetAddress = "Street Address format is invalid.";
      }
      if (!formData.city.trim()) {
        newErrors.city = "City is required.";
      }
      const zipRegex = /^\d{5}$/;
      if (!formData.zipCode.trim()) {
        newErrors.zipCode = "Zip code is required.";
      } else if (!zipRegex.test(formData.zipCode)) {
        newErrors.zipCode = "Zip code format is invalid.";
      }
    }
    else if (currentStep === 2) { // Energy Providers
      if (!formData.recentAssessment) {
        newErrors.recentAssessment = "Recent assessment is required.";
      }
      if (!formData.electricityProvider) {
        newErrors.electricityProvider = "Electricity provider is required.";
      }
      if (!formData.gasProvider) {
        newErrors.gasProvider = "Gas provider is required.";
      }
      if (!formData.fuelSource) {
        newErrors.fuelSource = "Fuel source is required.";
      }
    }
    else if (currentStep === 3) { // Household & Eligibility
      if (!formData.paymentAssistance) {
        newErrors.paymentAssistance = "Payment assistance is required.";
      }
      if (!formData.multiUnit) {
        newErrors.multiUnit = "Multi-unit is required.";
      }
      if (!formData.rentOwn) {
        newErrors.rentOwn = "Rent or Own is required.";
      }
      if (!formData.condo) {
        newErrors.condo = "Condo is required.";
      }
    }
    // (No additional validations for step 4 for now)

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper to format phone number as (NNN) NNN-NNNN
  function formatPhoneNumber(value) {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }

  // Add a function to convert phone numbers to E.164 format (OpenPhone compatible)
  function toE164(phone) {
    let digits = phone.replace(/\D/g, "");
    if (digits.startsWith("1") && digits.length === 11) {
      return "+" + digits;
    } else if (digits.length === 10) {
      return "+1" + digits;
    } else if (phone.startsWith("+")) {
      return phone;
    } else {
      // fallback: return as is
      return phone;
    }
  }

  // --- Parse a Google Place into Street/City/ZIP ---
  function parsePlaceAddress(place) {
    const out = { streetAddress: "", city: "", zipCode: "" };
    if (!place?.address_components) return out;

    const comps = place.address_components;
    const get = (type) => comps.find(c => c.types.includes(type))?.long_name || "";

    const streetNumber = get("street_number");
    const route = get("route");
    out.streetAddress = [streetNumber, route].filter(Boolean).join(" ").trim();

    out.city = get("locality") || get("sublocality") || get("postal_town") || "";
    out.zipCode = get("postal_code") || "";

    return out;
  }

  // Handle Next button
  const handleNext = () => {
    if (validateCurrentStep()) {
      if (currentStep === steps.length - 1) {
        // At the final step, check for special error conditions
        if (isErrorType1(formData)) {
          // Navigate to the "Thank you for your interest" error page
          const flags = getErrorType1Flags(formData);
          navigate("/error-type-1", { state: { flags, formData: { ...formData, phoneNumber: toE164(formData.phoneNumber) } } });
        } else {
          // Otherwise, survey is valid. Save formData for later pages and route to the scheduler.
          const formattedData = { ...formData, phoneNumber: toE164(formData.phoneNumber) };
          localStorage.setItem("userInfo", JSON.stringify(formattedData));
          navigate("/scheduler");
        }
      } else {
        setCurrentStep((prev) => prev + 1);
      }
    }
  };

  // Handle Back arrow
  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  // Handle input changes in real time
  const handleChange = (e) => {
    const { name, value,type, checked } = e.target;
    if (name === "phoneNumber") {
      setFormData((prev) => ({
        ...prev,
        [name]: formatPhoneNumber(value),
      }));
    } else if (type === "radio") {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };
  
  // Render the step content
  // TODO: collect phone number with survey
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="step-content">
            <div className="input-row">
              <label>First Name Utility Bill</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
              />
              {errors.firstName && <div className="error-text">{errors.firstName}</div>}
            </div>
            <div className="input-row">
              <label>Last Name Utility Bill</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
              />
              {errors.lastName && <div className="error-text">{errors.lastName}</div>}
            </div>
            <div className="input-row">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && <div className="error-text">{errors.email}</div>}
            </div>
            <div className="input-row">
              <label>Phone Number</label>
              <input
                type="text"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
              />
              {errors.phoneNumber && <div className="error-text">{errors.phoneNumber}</div>}
            </div>
          </div>
        );
      case 1:
        return (
          <div className="step-content">
            <div className="input-row">
              <label>Street Address</label>
              <input
                type="text"
                name="streetAddress"
                value={formData.streetAddress}
                onChange={handleChange}
                ref={streetInputRef}
                autoComplete="off"
              />
              {errors.streetAddress && <div className="error-text">{errors.streetAddress}</div>}
            </div>
            <div className="input-row">
              <label>City</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
              {errors.city && <div className="error-text">{errors.city}</div>}
            </div>
            <div className="input-row">
              <label>Zipcode</label>
              <input
                type="text"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
              />
              {errors.zipCode && <div className="error-text">{errors.zipCode}</div>}
            </div>
          </div>
        );
      case 2:
        return (
          <div className="step-content">
            <div className="input-row">
              <label>How long ago was your most recent Home Energy Assessment?</label>
              <select
                name="recentAssessment"
                value={formData.recentAssessment}
                onChange={handleChange}
              >
                <option value="">Select</option>
                <option value="never">I've never had a Home Energy Assessment</option>
                <option value="lt2">Less than 2 years ago</option>
                <option value="2t5">2 to 5 years ago</option>
                <option value="gt5">More than 5 years ago</option>
              </select>
              {errors.recentAssessment && <div className="error-text">{errors.recentAssessment}</div>}
            </div>
            <div className="input-row">
              <label>Who is your Electricity Provider?</label>
              <select
                name="electricityProvider"
                value={formData.electricityProvider}
                onChange={handleChange}
              >
                <option value="">Select</option>
                <option value="ever">Eversource</option>
                <option value="ngrid">National Grid</option>
                <option value="muni">Municipal</option>
              </select>
              {errors.electricityProvider && <div className="error-text">{errors.electricityProvider}</div>}
            </div>
            <div className="input-row">
              <label>Who is your Gas Provider?</label>
              <select
                name="gasProvider"
                value={formData.gasProvider}
                onChange={handleChange}
              >
                <option value="">Select</option>
                <option value="ngrid">National Grid</option>
                <option value="ever">Eversource</option>
                <option value="none">I don't have gas at my home</option>
              </select>
              {errors.gasProvider && <div className="error-text">{errors.gasProvider}</div>}
            </div>
            <div className="input-row">
              <label>What is your heating system's fuel source?</label>
              <select
                name="fuelSource"
                value={formData.fuelSource}
                onChange={handleChange}
              >
                <option value="">Select</option>
                <option value="gas">Natural Gas</option>
                <option value="oil">Oil</option>
                <option value="propane">Propane</option>
                <option value="electric">Electric</option>
              </select>
              {errors.fuelSource && <div className="error-text">{errors.fuelSource}</div>}
            </div>
          </div>
        );
      case 3:
        return (
          <div className="step-content">
            <div className="input-row">
              <label>Are you getting discounted rates from the utilities or any other payment assistance?</label>
              <select
                name="paymentAssistance"
                value={formData.paymentAssistance}
                onChange={handleChange}
              >
                <option value="">Select</option>
                <option value="n">No</option>
                <option value="y">Yes</option>
              </select>
              {errors.paymentAssistance && <div className="error-text">{errors.paymentAssistance}</div>}
            </div>
            <div className="input-row">
              <label>Is your home a multi-unit building?</label>
              <select
                name="multiUnit"
                value={formData.multiUnit}
                onChange={handleChange}
              >
                <option value="">Select</option>
                <option value="1">1 Unit</option>
                <option value="2">2 Units</option>
                <option value="3">3 Units</option>
                <option value="4">4 Units</option>
                <option value="5plus">5+ Units</option>
              </select>
              {errors.multiUnit && <div className="error-text">{errors.multiUnit}</div>}
            </div>
            <div className="input-row">
              <label>Do you rent or own your home?</label>
              <select
                name="rentOwn"
                value={formData.rentOwn}
                onChange={handleChange}
              >
                <option value="">Select</option>
                <option value="own">Own</option>
                <option value="rent">Rent</option>
              </select>
              {errors.rentOwn && <div className="error-text">{errors.rentOwn}</div>}
            </div>
            <div className="input-row">
              <label>Is your home located in a condo association of 5+ homes?</label>
              <select
                name="condo"
                value={formData.condo}
                onChange={handleChange}
              >
                <option value="">Select</option>
                <option value="n">No</option>
                <option value="y">Yes</option>
              </select>
              {errors.condo && <div className="error-text">{errors.condo}</div>}
            </div>
          </div>
        );
      case 4:
        return (
          <div className="step-content">
            {/* NEW: How did you hear about us? */}
            <div className="input-row">
              <label>How did you hear about us?</label>
              <select
                name="hearAbout"
                value={formData.hearAbout}
                onChange={handleChange}
              >
                <option value="">Select</option>
                <option value="website">Website</option>
                <option value="referral">Referral</option>
                <option value="event">Event</option>
                <option value="doorknocking">Door Knocking</option>
                <option value="mailer">Mailer or Flyer</option>
                <option value="email">Email</option>
                <option value="ai">AI Search</option>
                <option value="facebook">Facebook</option>
                <option value="instagram">Instagram</option>
                <option value="linkedin">LinkedIn</option>
                <option value="other">Other (Please Specify)</option>
              </select>

              {formData.hearAbout === "other" && (
                <input
                  type="text"
                  name="hearAboutOther"
                  placeholder="Please specify"
                  value={formData.hearAboutOther}
                  onChange={handleChange}
                  style={{ marginTop: 8 }}
                />
              )}

              {errors.hearAbout && <div className="error-text">{errors.hearAbout}</div>}
            </div>
              
{/* Heat Pump Interest */}
<div className="input-row">
  <label>Are you interested in a heat pump?</label>
  <div style={{ display: 'flex', gap: '20px', marginTop: '8px' }}>
    <label className="checkbox-container">
      <input
        type="checkbox"
        checked={formData.heatPumpInterest === "y"}
        onChange={() => setFormData(prev => ({ ...prev, heatPumpInterest: "y" }))}
      />
      <span className="checkmark"></span>
      Yes
    </label>
    <label className="checkbox-container">
      <input
        type="checkbox"
        checked={formData.heatPumpInterest === "n"}
        onChange={() => setFormData(prev => ({ ...prev, heatPumpInterest: "n" }))}
      />
      <span className="checkmark"></span>
      No
    </label>
  </div>
</div>
            {/* NEW: Preferred Language */}
            <div className="input-row">
              <label>Preferred Language</label>
              <select
                name="preferredLanguage"
                value={formData.preferredLanguage}
                onChange={handleChange}
              >
                <option value="">Select</option>
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="pt">Portuguese</option>
                <option value="vi">Vietnamese</option>
                <option value="zh_mandarin">Mandarin</option>
                <option value="zh_cantonese">Cantonese</option>
                <option value="ht">Haitian Creole</option>
                <option value="other">Other (Please Specify)</option>
              </select>

              {formData.preferredLanguage === "other" && (
                <input
                  type="text"
                  name="preferredLanguageOther"
                  placeholder="Please specify"
                  value={formData.preferredLanguageOther}
                  onChange={handleChange}
                  style={{ marginTop: 8 }}
                />
              )}

              {errors.preferredLanguage && <div className="error-text">{errors.preferredLanguage}</div>}
            </div>

            <div className="input-row">
              <label>Any other information that you would like to share?</label>
              <input
                type="text"
                name="otherInfo"
                value={formData.otherInfo}
                onChange={handleChange}
              />
            </div>
            <div className="input-row">
              <label>Referral Code (if applicable)</label>
              <input
                type="text"
                name="referralCode"
                value={formData.referralCode}
                onChange={handleChange}
              />
              {errors.referralCode && <div className="error-text">{errors.referralCode}</div>}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const posthog = usePostHog();
  const prevStepRef = useRef(currentStep);
  useEffect(() => {
    if (posthog && prevStepRef.current !== currentStep) {
      posthog.capture('survey_step_viewed', {
        step: steps[currentStep],
        step_index: currentStep,
      });
      prevStepRef.current = currentStep;
    }
  }, [currentStep, posthog, steps]);

  return (
    <div className="app-container">
      {/* Top bar with company logo and progress bar */}
      <div className="top-bar">
        <div className="logo">
          <img src="/colt-single-logo.png" alt=""/>
        </div>
        <div className="progress-bar">
          {steps.map((stepLabel, index) => {
            let parallelogramClass = "";
            if (index < currentStep) parallelogramClass = "previous-step";
            else if (index === currentStep) parallelogramClass = "current-step";
            else parallelogramClass = "future-step";

            return (
              <div key={index} className={`parallelogram ${parallelogramClass}`}>
                <span className="parallelogram-text">{stepLabel}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Survey content */}
      <div className="survey-content">
        {renderStepContent()}

        {/* Navigation buttons */}
        <div className="navigation-buttons">
          {currentStep > 0 && (
            <button className="back-arrow" onClick={handleBack}>
              &#8592;
            </button>
          )}
          {currentStep < steps.length - 1 && (
            <button className="next-button" onClick={handleNext}>
              Next
            </button>
          )}
          {currentStep === steps.length - 1 && (
            <button className="next-button" onClick={handleNext}>
              Finish
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Survey;
